import React, { useEffect, useMemo, useState } from 'react';
import ListControls, { EmptyFilterState } from './ListControls';
import ContextualHelpPanel from './ContextualHelpPanel';
import {
  CLIENT_SORT_OPTIONS,
  createClientControls,
  getClientListView,
  getOptionLabel,
} from '../navigationUtils';

const createEmptyFormData = () => ({
  name: '',
  location: '',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  notes: '',
});

const ClientManager = ({
  clients,
  onAdd,
  onUpdate,
  onDelete,
  onSelect,
  controls,
  onControlsChange,
  helpConfig,
  openFormRequest = 0,
  formCounts = {},
}) => {
  const [showForm, setShowForm] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [formData, setFormData] = useState(createEmptyFormData());
  const normalizedControls = createClientControls(controls);
  const clientView = useMemo(() => getClientListView(clients, normalizedControls), [clients, normalizedControls]);

  useEffect(() => {
    if (openFormRequest > 0) {
      setEditingClient(null);
      setFormData(createEmptyFormData());
      setShowForm(true);
    }
  }, [openFormRequest]);

  const activeChips = [];

  if (normalizedControls.search.trim()) {
    activeChips.push({
      id: 'search',
      label: `Search: ${normalizedControls.search.trim()}`,
      onRemove: () => onControlsChange({ search: '' }),
    });
  }

  if (normalizedControls.sortBy !== 'name-asc') {
    activeChips.push({
      id: 'sort',
      label: `Sort: ${getOptionLabel(CLIENT_SORT_OPTIONS, normalizedControls.sortBy)}`,
      onRemove: () => onControlsChange({ sortBy: 'name-asc' }),
    });
  }

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (editingClient) {
      await onUpdate(editingClient.id, formData);
      setEditingClient(null);
    } else {
      await onAdd(formData);
    }

    setFormData(createEmptyFormData());
    setShowForm(false);
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({
      name: client.name,
      location: client.location || '',
      contactName: client.contactName || '',
      contactEmail: client.contactEmail || '',
      contactPhone: client.contactPhone || '',
      notes: client.notes || '',
    });
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingClient(null);
    setFormData(createEmptyFormData());
  };

  return (
    <div>
      <div className="card">
        <div className="df-toolbar">
          <div className="df-toolbar-title">
            <h2 className="card-title">Clients</h2>
          </div>
          {!showForm && (
            <button className="btn btn-primary" onClick={() => setShowForm(true)}>
              + Add New Client
            </button>
          )}
        </div>

        {helpConfig && <ContextualHelpPanel {...helpConfig} />}

        {showForm && (
          <form onSubmit={handleSubmit} style={{ marginBottom: '20px', padding: '20px', background: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: '15px', color: 'var(--primary)' }}>
              {editingClient ? 'Edit Client' : 'Add New Client'}
            </h3>
            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">Client Name *</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.name}
                  onChange={(event) => setFormData({ ...formData, name: event.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Location</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.location}
                  onChange={(event) => setFormData({ ...formData, location: event.target.value })}
                  placeholder="City, State, Country"
                />
              </div>
            </div>
            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">Contact Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.contactName}
                  onChange={(event) => setFormData({ ...formData, contactName: event.target.value })}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Contact Email</label>
                <input
                  type="email"
                  className="form-input"
                  value={formData.contactEmail}
                  onChange={(event) => setFormData({ ...formData, contactEmail: event.target.value })}
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Contact Phone</label>
              <input
                type="text"
                className="form-input"
                value={formData.contactPhone}
                onChange={(event) => setFormData({ ...formData, contactPhone: event.target.value })}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Notes</label>
              <textarea
                className="form-textarea"
                value={formData.notes}
                onChange={(event) => setFormData({ ...formData, notes: event.target.value })}
                rows={3}
              />
            </div>
            <div className="df-action-group">
              <button type="submit" className="btn btn-primary">
                {editingClient ? 'Update Client' : 'Add Client'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                Cancel
              </button>
            </div>
          </form>
        )}

        {clients.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            <p>No clients yet. Add your first client to get started.</p>
          </div>
        ) : (
          <div className="df-list-layout">
            <ListControls
              searchPlaceholder="Search by client name, location, contact, or notes"
              searchValue={normalizedControls.search}
              onSearchChange={(search) => onControlsChange({ search })}
              filters={[]}
              sortValue={normalizedControls.sortBy}
              sortOptions={CLIENT_SORT_OPTIONS}
              onSortChange={(sortBy) => onControlsChange({ sortBy })}
              shownCount={clientView.shownCount}
              totalCount={clientView.totalCount}
              resultLabel="clients"
              chips={activeChips}
              onClear={() => onControlsChange(createClientControls())}
            />

            {clientView.shownCount === 0 ? (
              <EmptyFilterState
                title="No clients match the current filters"
                description="Try a broader search or clear the filters to see the full client list again."
                onClear={() => onControlsChange(createClientControls())}
              />
            ) : (
              <div className="df-group-stack">
                {clientView.groups.map((group) => (
                  <section key={group.key} className="df-group-section">
                    <div className="df-group-header">
                      <span>{group.label}</span>
                      <span>{group.items.length}</span>
                    </div>
                    <div className="df-list-stack">
                      {group.items.map((client) => {
                        const linkedFormCount = formCounts[client.id] || 0;
                        const subtitleParts = [];

                        if (client.location) {
                          subtitleParts.push(`Location: ${client.location}`);
                        }

                        if (client.contactName) {
                          subtitleParts.push(`Contact: ${client.contactName}`);
                        }

                        if (client.contactEmail) {
                          subtitleParts.push(`Email: ${client.contactEmail}`);
                        }

                        subtitleParts.push(
                          linkedFormCount === 1 ? '1 linked form' : `${linkedFormCount} linked forms`
                        );

                        return (
                          <div key={client.id} className="list-item">
                            <div className="list-item-info">
                              <div className="list-item-title">{client.name}</div>
                              <div className="list-item-subtitle">{subtitleParts.join(' | ')}</div>
                            </div>
                            <div className="df-action-group">
                              <button className="btn btn-primary btn-sm" onClick={() => onSelect(client)}>
                                View Forms
                              </button>
                              <button className="btn btn-secondary btn-sm" onClick={() => handleEdit(client)}>
                                Edit
                              </button>
                              <button className="btn btn-danger btn-sm" onClick={() => onDelete(client.id)}>
                                Delete
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </section>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientManager;


